import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

import {
  clubCareerService,
  performanceStatsService,
  transferHistoryService,
  nationalTeamService,
  advancedPerformanceService,
  medicalRecordsService,
  competitionsPlayedService,
  marketValueHistoryService,
  playerMediaService } from
'../../../services/playerMasterService';


const PlayerDetailModal = ({ player, onClose, onRequestConsultation, isOpen }) => {
  const currentPlayer = player;
  const [activeTab, setActiveTab] = useState('basic');
  const [careerHistory, setCareerHistory] = useState([]);
  const [performanceStats, setPerformanceStats] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [nationalTeam, setNationalTeam] = useState(null);
  const [advancedPerformance, setAdvancedPerformance] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [marketValueHistory, setMarketValueHistory] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (player?.id && isOpen) {
      fetchPlayerDetails();
    }
  }, [player?.id, isOpen]);

  const fetchPlayerDetails = async () => {
    setLoading(true);
    try {
      const [careerRes, statsRes, transferRes, nationalRes, advancedRes, medicalRes, competitionsRes, marketValueRes, mediaRes] = await Promise.all([
      clubCareerService?.getByPlayerId(player?.id),
      performanceStatsService?.getByPlayerId(player?.id),
      transferHistoryService?.getByPlayerId(player?.id),
      nationalTeamService?.getSeniorByPlayerId(player?.id),
      advancedPerformanceService?.getByPlayerId(player?.id),
      medicalRecordsService?.getByPlayerId(player?.id),
      competitionsPlayedService?.getByPlayerId(player?.id),
      marketValueHistoryService?.getByPlayerId(player?.id),
      playerMediaService?.getByPlayerId(player?.id)]
      );
      setCareerHistory(careerRes?.data || []);
      setPerformanceStats(statsRes?.data || []);
      setTransferHistory(transferRes?.data || []);
      setNationalTeam(nationalRes?.data);
      setAdvancedPerformance(advancedRes?.data || []);
      setMedicalRecords(medicalRes?.data || []);
      setCompetitions(competitionsRes?.data || []);
      setMarketValueHistory(marketValueRes?.data || []);
      setMedia(mediaRes?.data || []);
    } catch (error) {
      console.error('Error fetching player data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!player || !isOpen) return null;

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today?.getDate() < birthDate?.getDate()) {
      age--;
    }
    return age;
  };

  const formatPosition = (position) => {
    if (!position) return 'N/A';
    return position?.split('-')?.map((word) =>
    word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // FIX: Use stat.appearances (not stat.matchesPlayed) to match the data source used in PlayerCard
  const calculateCareerTotals = () => {
    if (!performanceStats || performanceStats?.length === 0) {
      return {
        totalMatches: player?.totalAppearances || 0,
        totalGoals: player?.goalsScored || 0,
        totalAssists: player?.assists || 0,
        averageRating: player?.averageRating || 0
      };
    }

    const totals = performanceStats?.reduce((acc, stat) => {
      return {
        totalMatches: acc?.totalMatches + (stat?.appearances || 0),
        totalGoals: acc?.totalGoals + (stat?.goalsScored || 0),
        totalAssists: acc?.totalAssists + (stat?.assists || 0),
        totalRatings: acc?.totalRatings + (stat?.motmAwards || 0),
        count: acc?.count + (stat?.motmAwards ? 1 : 0)
      };
    }, { totalMatches: 0, totalGoals: 0, totalAssists: 0, totalRatings: 0, count: 0 });

    return {
      totalMatches: totals?.totalMatches,
      totalGoals: totals?.totalGoals,
      totalAssists: totals?.totalAssists,
      averageRating: totals?.count > 0 ? (totals?.totalRatings / totals?.count)?.toFixed(1) : player?.averageRating || 0
    };
  };

  const careerTotals = calculateCareerTotals();

  const tabs = [
  { id: 'basic', label: 'Basic Info', icon: 'User' },
  { id: 'club', label: 'Club Career', icon: 'Shield' },
  { id: 'market', label: 'Market Data', icon: 'TrendingUp' },
  { id: 'agent', label: 'Agent Info', icon: 'Briefcase' },
  { id: 'status', label: 'Status', icon: 'Activity' },
  { id: 'career', label: 'Career History', icon: 'Clock' },
  { id: 'performance', label: 'Performance Stats', icon: 'BarChart3' },
  { id: 'advanced', label: 'Advanced Performance', icon: 'Zap' },
  { id: 'competitions', label: 'Competitions', icon: 'Trophy' },
  { id: 'transfers', label: 'Transfer History', icon: 'ArrowLeftRight' },
  { id: 'national', label: 'National Team', icon: 'Flag' },
  { id: 'medical', label: 'Medical Records', icon: 'Heart' },
  { id: 'markethistory', label: 'Market Value History', icon: 'LineChart' },
  { id: 'media', label: 'Media & Documents', icon: 'Image' }];


  const InfoRow = ({ label, value }) =>
  <div className="flex justify-between items-start py-2 border-b last:border-0" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
      <span className="text-sm font-medium min-w-[140px]" style={{ color: 'rgba(180,210,255,0.6)' }}>{label}</span>
      <span className="text-sm font-semibold text-right" style={{ color: 'rgba(255,255,255,0.9)' }}>{value || 'N/A'}</span>
    </div>;


  const SectionCard = ({ title, icon, children }) =>
  <div className="rounded-lg p-4 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.9)' }}>
        <Icon name={icon} size={18} color="#FF6B00" />
        {title}
      </h4>
      {children}
    </div>;


  const EmptyState = ({ message }) =>
  <div className="text-center py-8 text-muted-foreground">
      <Icon name="Database" size={32} className="mx-auto mb-2 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>;


  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-4">
            <SectionCard title="Personal Information" icon="User">
              <InfoRow label="Full Name" value={currentPlayer?.fullName} />
              <InfoRow label="Known As" value={currentPlayer?.knownAs} />
              <InfoRow label="Date of Birth" value={formatDate(currentPlayer?.dateOfBirth)} />
              <InfoRow label="Age" value={calculateAge(currentPlayer?.dateOfBirth) ? `${calculateAge(currentPlayer?.dateOfBirth)} years` : null} />
              <InfoRow label="Place of Birth" value={currentPlayer?.placeOfBirth} />
              <InfoRow label="Nationality" value={currentPlayer?.nationalityPrimary} />
              <InfoRow label="Secondary Nationality" value={currentPlayer?.nationalitySecondary} />
            </SectionCard>
            <SectionCard title="Physical Attributes" icon="Activity">
              <InfoRow label="Height" value={currentPlayer?.height ? `${currentPlayer?.height} cm` : null} />
              <InfoRow label="Weight" value={currentPlayer?.weight ? `${currentPlayer?.weight} kg` : null} />
              <InfoRow label="Preferred Foot" value={currentPlayer?.preferredFoot} />
              <InfoRow label="Primary Position" value={formatPosition(currentPlayer?.primaryPosition)} />
              <InfoRow label="Secondary Positions" value={currentPlayer?.secondaryPositions} />
            </SectionCard>
            <SectionCard title="Contact Information" icon="Phone">
              <InfoRow label="Email" value={currentPlayer?.email} />
              <InfoRow label="Phone" value={currentPlayer?.phone} />
              <InfoRow label="City" value={currentPlayer?.city} />
              <InfoRow label="Country" value={currentPlayer?.country} />
            </SectionCard>
          </div>);


      case 'club':
        return (
          <div className="space-y-4">
            <SectionCard title="Current Club" icon="Shield">
              <InfoRow label="Club" value={currentPlayer?.currentClub || 'Free Agent'} />
              <InfoRow label="League" value={currentPlayer?.currentLeague} />
              <InfoRow label="Country" value={currentPlayer?.currentCountry} />
              <InfoRow label="Squad Number" value={currentPlayer?.squadNumber ? `#${currentPlayer?.squadNumber}` : null} />
              <InfoRow label="Contract Type" value={currentPlayer?.contractType} />
              <InfoRow label="Contract Start" value={formatDate(currentPlayer?.contractStartDate)} />
              <InfoRow label="Contract End" value={formatDate(currentPlayer?.contractEndDate)} />
            </SectionCard>
            <SectionCard title="Career Statistics" icon="BarChart3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg p-3 text-center" style={{ background: 'rgba(0,100,255,0.15)', border: '1px solid rgba(0,100,255,0.3)' }}>
                  <div className="text-2xl font-black" style={{ color: '#60a5fa' }}>{careerTotals?.totalMatches}</div>
                  <div className="text-xs font-semibold mt-1" style={{ color: 'rgba(150,190,255,0.8)' }}>Matches</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: 'rgba(0,180,80,0.15)', border: '1px solid rgba(0,180,80,0.3)' }}>
                  <div className="text-2xl font-black" style={{ color: '#34d399' }}>{careerTotals?.totalGoals}</div>
                  <div className="text-xs font-semibold mt-1" style={{ color: 'rgba(100,220,160,0.8)' }}>Goals</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: 'rgba(150,50,255,0.15)', border: '1px solid rgba(150,50,255,0.3)' }}>
                  <div className="text-2xl font-black" style={{ color: '#a78bfa' }}>{careerTotals?.totalAssists}</div>
                  <div className="text-xs font-semibold mt-1" style={{ color: 'rgba(180,150,255,0.8)' }}>Assists</div>
                </div>
                <div className="rounded-lg p-3 text-center" style={{ background: 'rgba(255,180,0,0.15)', border: '1px solid rgba(255,180,0,0.3)' }}>
                  <div className="text-2xl font-black" style={{ color: '#fbbf24' }}>{careerTotals?.averageRating || 'N/A'}</div>
                  <div className="text-xs font-semibold mt-1" style={{ color: 'rgba(255,200,100,0.8)' }}>Avg Rating</div>
                </div>
              </div>
            </SectionCard>
          </div>);


      case 'market':
        return (
          <div className="space-y-4">
            <SectionCard title="Market Valuation" icon="TrendingUp">
              <InfoRow label="Current Market Value" value={currentPlayer?.currentMarketValue ? `€${currentPlayer?.currentMarketValue}M` : null} />
              <InfoRow label="Highest Market Value" value={currentPlayer?.highestMarketValue ? `€${currentPlayer?.highestMarketValue}M` : null} />
              <InfoRow label="Available for Transfer" value={currentPlayer?.availableForTransfer ? 'Yes' : 'No'} />
              <InfoRow label="Available for Loan" value={currentPlayer?.availableForLoan ? 'Yes' : 'No'} />
              {/* Permanent FIFA Verification Badge */}
              <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm text-muted-foreground font-medium">FIFA Verification</span>
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1">
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1f42af579-1765038506846.png"
                    alt="FIFA"
                    className="h-4 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-xs font-bold text-blue-700">FIFA Verified</span>
                </div>
              </div>
              {/* Permanent CAF Verification Badge */}
              <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm text-muted-foreground font-medium">CAF Verification</span>
                <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1deb3e038-1766846666598.png"
                    alt="CAF"
                    className="h-4 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-xs font-bold text-green-700">CAF Verified</span>
                </div>
              </div>
            </SectionCard>
          </div>);


      case 'agent':
        return (
          <div className="space-y-4">
            <SectionCard title="Agent / Representation" icon="Briefcase">
              <InfoRow label="Agent Name" value={currentPlayer?.agentName} />
              <InfoRow label="Agency" value={currentPlayer?.agencyName} />
              <InfoRow label="Agent Email" value={currentPlayer?.agentEmail} />
              <InfoRow label="Agent Phone" value={currentPlayer?.agentPhone} />
              <InfoRow label="Representation Until" value={formatDate(currentPlayer?.representationContractUntil)} />
            </SectionCard>
            <SectionCard title="Emergency Contact" icon="Phone">
              <InfoRow label="Contact Name" value={currentPlayer?.emergencyContactName} />
              <InfoRow label="Relation" value={currentPlayer?.emergencyContactRelation} />
              <InfoRow label="Phone" value={currentPlayer?.emergencyContactPhone} />
            </SectionCard>
          </div>);


      case 'status':
        return (
          <div className="space-y-4">
            <SectionCard title="Player Status" icon="Activity">
              <InfoRow label="Transfer Status" value={currentPlayer?.availableForTransfer ? '✅ Available for Transfer' : '❌ Not Available'} />
              <InfoRow label="Loan Status" value={currentPlayer?.availableForLoan ? '✅ Available for Loan' : '❌ Not Available'} />
              {/* FIFA Verified - always permanent green */}
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                <span className="text-sm font-medium" style={{ color: 'rgba(180,210,255,0.6)' }}>FIFA Verified</span>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,120,255,0.15)', border: '1px solid rgba(0,120,255,0.4)' }}>
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1f42af579-1765038506846.png"
                    alt="FIFA"
                    className="h-4 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-xs font-bold" style={{ color: '#6699ff' }}>✅ FIFA Verified</span>
                </div>
              </div>
              {/* CAF Verified - always permanent green */}
              <div className="flex justify-between items-center py-2" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                <span className="text-sm font-medium" style={{ color: 'rgba(180,210,255,0.6)' }}>CAF Verified</span>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,166,81,0.15)', border: '1px solid rgba(0,166,81,0.4)' }}>
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1deb3e038-1766846666598.png"
                    alt="CAF"
                    className="h-4 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span className="text-xs font-bold" style={{ color: '#00d68f' }}>✅ CAF Verified</span>
                </div>
              </div>
              <InfoRow label="Contract Status" value={currentPlayer?.contractEndDate ? `Active until ${formatDate(currentPlayer?.contractEndDate)}` : 'No Contract'} />
            </SectionCard>
            <SectionCard title="Additional Notes" icon="FileText">
              <p className="text-sm text-foreground">{currentPlayer?.additionalNotes || 'No additional notes available.'}</p>
            </SectionCard>
            <SectionCard title="Medical Conditions" icon="Heart">
              <p className="text-sm text-foreground">{currentPlayer?.medicalConditions || 'No medical conditions on record.'}</p>
            </SectionCard>
          </div>);


      case 'career':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            careerHistory?.length === 0 ?
            <EmptyState message="No career history records found" /> :

            careerHistory?.map((career, idx) =>
            <SectionCard key={idx} title={`${career?.clubName || 'Unknown Club'} (${career?.season || 'N/A'})`} icon="Shield">
                  <InfoRow label="Club" value={career?.clubName} />
                  <InfoRow label="League" value={career?.leagueName} />
                  <InfoRow label="Country" value={career?.country} />
                  <InfoRow label="Season" value={career?.season} />
                  <InfoRow label="Appearances" value={career?.appearances} />
                  <InfoRow label="Goals" value={career?.goalsScored} />
                  <InfoRow label="Assists" value={career?.assists} />
                  <InfoRow label="Minutes Played" value={career?.minutesPlayed} />
                </SectionCard>
            )
            }
          </div>);


      case 'performance':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            performanceStats?.length === 0 ?
            <EmptyState message="No performance stats records found" /> :

            performanceStats?.map((stat, idx) =>
            <SectionCard key={idx} title={`${stat?.season || 'N/A'} - ${stat?.competitionType || 'All Competitions'}`} icon="BarChart3">
                  <InfoRow label="Season" value={stat?.season} />
                  <InfoRow label="Competition" value={stat?.competitionType} />
                  <InfoRow label="Appearances" value={stat?.appearances} />
                  <InfoRow label="Games Started" value={stat?.gamesStarted} />
                  <InfoRow label="Games as Sub" value={stat?.gamesSubstitute} />
                  <InfoRow label="Minutes Played" value={stat?.minutesPlayed} />
                  <InfoRow label="Goals" value={stat?.goalsScored} />
                  <InfoRow label="Assists" value={stat?.assists} />
                  <InfoRow label="Yellow Cards" value={stat?.yellowCards} />
                  <InfoRow label="Red Cards" value={stat?.redCards} />
                  <InfoRow label="MOTM Awards" value={stat?.motmAwards} />
                </SectionCard>
            )
            }
          </div>);


      case 'advanced':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            advancedPerformance?.length === 0 ?
            <EmptyState message="No advanced performance data found" /> :

            advancedPerformance?.map((adv, idx) =>
            <SectionCard key={idx} title={`${adv?.season || 'N/A'} Advanced Stats`} icon="Zap">
                  <InfoRow label="Season" value={adv?.season} />
                  <InfoRow label="xG" value={adv?.xg} />
                  <InfoRow label="xA" value={adv?.xa} />
                  <InfoRow label="Dribbles Completed" value={adv?.dribblesCompleted} />
                  <InfoRow label="Key Passes" value={adv?.keyPasses} />
                  <InfoRow label="Tackles Won" value={adv?.tacklesWon} />
                  <InfoRow label="Interceptions" value={adv?.interceptions} />
                  <InfoRow label="Aerial Duels Won" value={adv?.aerialDuelsWon} />
                  <InfoRow label="Pass Accuracy" value={adv?.passAccuracy ? `${adv?.passAccuracy}%` : null} />
                  <InfoRow label="Shot Accuracy" value={adv?.shotAccuracy ? `${adv?.shotAccuracy}%` : null} />
                  <InfoRow label="Distance Covered" value={adv?.distanceCovered ? `${adv?.distanceCovered} km` : null} />
                </SectionCard>
            )
            }
          </div>);


      case 'competitions':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            competitions?.length === 0 ?
            <EmptyState message="No competition records found" /> :

            competitions?.map((comp, idx) =>
            <SectionCard key={idx} title={comp?.competitionName || 'Competition'} icon="Trophy">
                  <InfoRow label="Competition" value={comp?.competitionName} />
                  <InfoRow label="Type" value={comp?.competitionType} />
                  <InfoRow label="Season" value={comp?.season} />
                  <InfoRow label="Appearances" value={comp?.appearances} />
                  <InfoRow label="Goals" value={comp?.goals} />
                  <InfoRow label="Assists" value={comp?.assists} />
                  <InfoRow label="Minutes Played" value={comp?.minutesPlayed} />
                </SectionCard>
            )
            }
          </div>);


      case 'transfers':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            transferHistory?.length === 0 ?
            <EmptyState message="No transfer history found" /> :

            transferHistory?.map((transfer, idx) =>
            <SectionCard key={idx} title={`${transfer?.fromClub || 'Unknown'} → ${transfer?.toClub || 'Unknown'}`} icon="ArrowLeftRight">
                  <InfoRow label="From Club" value={transfer?.fromClub} />
                  <InfoRow label="To Club" value={transfer?.toClub} />
                  <InfoRow label="Transfer Date" value={formatDate(transfer?.transferDate)} />
                  <InfoRow label="Transfer Type" value={transfer?.transferType} />
                  <InfoRow label="Fee" value={transfer?.transferFee ? `€${transfer?.transferFee}M` : 'Undisclosed'} />
                  <InfoRow label="Season" value={transfer?.season} />
                </SectionCard>
            )
            }
          </div>);


      case 'national':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            !nationalTeam ?
            <EmptyState message="No national team data found" /> :

            <SectionCard title="National Team" icon="Flag">
                <InfoRow label="Country" value={nationalTeam?.country} />
                <InfoRow label="Total Caps" value={nationalTeam?.capsTotal} />
                <InfoRow label="Goals" value={nationalTeam?.goalsTotal} />
                <InfoRow label="Assists" value={nationalTeam?.assistsTotal} />
                <InfoRow label="First Cap" value={formatDate(nationalTeam?.firstCapDate)} />
                <InfoRow label="Last Cap" value={formatDate(nationalTeam?.lastCapDate)} />
                <InfoRow label="Status" value={nationalTeam?.currentlyActive ? 'Active' : 'Retired'} />
              </SectionCard>
            }
          </div>);


      case 'medical':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            medicalRecords?.length === 0 ?
            <EmptyState message="No medical records found" /> :

            medicalRecords?.map((record, idx) =>
            <SectionCard key={idx} title={record?.injuryType || 'Medical Record'} icon="Heart">
                  <InfoRow label="Injury Type" value={record?.injuryType} />
                  <InfoRow label="Body Part" value={record?.bodyPart} />
                  <InfoRow label="Severity" value={record?.severity} />
                  <InfoRow label="Start Date" value={formatDate(record?.injuryStartDate)} />
                  <InfoRow label="Recovery Date" value={formatDate(record?.recoveryDate)} />
                  <InfoRow label="Games Missed" value={record?.gamesMissed} />
                  <InfoRow label="Treatment" value={record?.treatment} />
                </SectionCard>
            )
            }
          </div>);


      case 'markethistory':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            marketValueHistory?.length === 0 ?
            <EmptyState message="No market value history found" /> :

            <SectionCard title="Market Value History" icon="LineChart">
                <div className="space-y-2">
                  {marketValueHistory?.map((entry, idx) =>
                <div key={idx} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                      <span className="text-sm text-muted-foreground">{formatDate(entry?.valuationDate)}</span>
                      <span className="text-sm font-bold text-emerald-600">€{entry?.marketValue}M</span>
                    </div>
                )}
                </div>
              </SectionCard>
            }
          </div>);


      case 'media':
        return (
          <div className="space-y-4">
            {loading ?
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div> :
            media?.length === 0 ?
            <EmptyState message="No media or documents found" /> :

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {media?.map((item, idx) =>
              <div key={idx} className="bg-muted/30 rounded-lg p-4 border border-border flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.mediaType === 'video' ? 'Video' : item?.mediaType === 'document' ? 'FileText' : 'Image'} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{item?.title || item?.fileName || 'Media File'}</p>
                      <p className="text-xs text-muted-foreground capitalize">{item?.mediaType || 'file'}</p>
                      {item?.fileUrl &&
                  <a href={item?.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-block">View File</a>
                  }
                    </div>
                  </div>
              )}
              </div>
            }
          </div>);


      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col"
            style={{ background: '#0d1117', border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 25px 80px rgba(0,0,0,0.7)' }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full transition-all hover:scale-110"
              style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <Icon name="X" size={20} color="white" />
            </button>

            {/* Hero Section - Premium Dark Football */}
            <div className="relative h-48 overflow-hidden flex-shrink-0" style={{ background: 'linear-gradient(135deg, #060810 0%, #0d1b3e 50%, #081a10 100%)' }}>
              {/* Football field lines */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full" style={{ border: '1px solid rgba(255,255,255,0.04)' }} />
                <div className="absolute top-0 bottom-0 left-1/2 w-px" style={{ background: 'rgba(255,255,255,0.03)' }} />
                <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.018) 40px, rgba(255,255,255,0.018) 41px)' }} />
              </div>
              {/* Glow orbs */}
              <div className="absolute top-0 left-0 w-72 h-72 rounded-full blur-3xl" style={{ background: 'rgba(0,100,255,0.12)' }} />
              <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full blur-3xl" style={{ background: 'rgba(0,180,80,0.08)' }} />
              {/* EA Sports + PS5 logos in hero */}
              <div className="absolute top-5 left-6 flex items-center gap-4">
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(15px)', border: '1px solid rgba(255,107,0,0.4)' }}>
                  <svg viewBox="0 0 90 30" width="72" height="24">
                    <text x="0" y="12" fontFamily="Arial Black, sans-serif" fontSize="8" fontWeight="900" fill="#FF6B00" letterSpacing="1">EA SPORTS</text>
                    <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontSize="18" fontWeight="900" fill="white">FC</text>
                    <rect x="24" y="17" width="66" height="2" fill="#FF6B00"/>
                    <text x="26" y="27" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="700" fill="#FF6B00" letterSpacing="2">24/25</text>
                  </svg>
                </div>
                <div className="flex items-center px-3 py-2 rounded-xl" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(15px)', border: '1px solid rgba(0,100,255,0.4)' }}>
                  <svg viewBox="0 0 55 24" width="44" height="19">
                    <text x="0" y="20" fontFamily="Arial Black, sans-serif" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">PS5</text>
                  </svg>
                </div>
              </div>
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #0d1117 0%, transparent 60%)' }} />
            </div>

            {/* Profile Header */}
            <div className="relative px-6 -mt-16 pb-4 flex-shrink-0">
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="flex-shrink-0">
                  <div
                    className="relative w-28 h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden shadow-2xl"
                    style={{ border: '3px solid rgba(255,107,0,0.5)', boxShadow: '0 0 30px rgba(255,107,0,0.25)' }}
                  >
                    <Image
                      src={currentPlayer?.profilePhotoUrl || currentPlayer?.profilePhoto || '/assets/images/no_image.png'}
                      alt={`${currentPlayer?.fullName} profile photo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1 pt-16 md:pt-4">
                  <h2 className="text-2xl md:text-3xl font-black text-white">{currentPlayer?.fullName}</h2>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-bold text-white"
                      style={{ background: 'rgba(0,120,255,0.25)', border: '1px solid rgba(0,120,255,0.45)' }}
                    >
                      {formatPosition(currentPlayer?.primaryPosition)}
                    </span>
                    {currentPlayer?.nationalityPrimary && (
                      <span className="px-3 py-1 rounded-full text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.65)' }}>
                        {currentPlayer?.nationalityPrimary}
                      </span>
                    )}
                    {calculateAge(currentPlayer?.dateOfBirth) && (
                      <span className="px-3 py-1 rounded-full text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.65)' }}>
                        {calculateAge(currentPlayer?.dateOfBirth)} yrs
                      </span>
                    )}
                    {currentPlayer?.currentClub && (
                      <span className="px-3 py-1 rounded-full text-sm font-bold" style={{ background: 'rgba(0,214,143,0.18)', border: '1px solid rgba(0,214,143,0.38)', color: '#00d68f' }}>
                        {currentPlayer?.currentClub}
                      </span>
                    )}
                  </div>
                  {/* Permanent FIFA & CAF Verification Badges */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,120,255,0.12)', border: '1px solid rgba(0,120,255,0.38)' }}>
                      <img
                        src="https://img.rocket.new/generatedImages/rocket_gen_img_1f42af579-1765038506846.png"
                        alt="FIFA"
                        className="h-4 w-auto object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="text-xs font-semibold" style={{ color: '#6699ff' }}>FIFA Verified</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,166,81,0.12)', border: '1px solid rgba(0,166,81,0.38)' }}>
                      <img
                        src="https://img.rocket.new/generatedImages/rocket_gen_img_1deb3e038-1766846666598.png"
                        alt="CAF"
                        className="h-4 w-auto object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="text-xs font-semibold" style={{ color: '#00d68f' }}>CAF Verified</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation - Scrollable */}
            <div className="flex-shrink-0 overflow-x-auto" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex px-4 gap-1 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab?.id
                        ? 'border-orange-500 text-orange-400' :'border-transparent hover:text-white'
                    }`}
                    style={{ color: activeTab === tab?.id ? '#FB923C' : 'rgba(255,255,255,0.4)' }}
                  >
                    <Icon name={tab?.icon} size={14} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6" style={{ background: '#0d1117' }}>
              {renderTabContent()}
            </div>

            {/* Footer Actions */}
            <div
              className="flex-shrink-0 px-6 py-4 flex justify-between items-center"
              style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: '#0a0e14' }}
            >
              <div className="text-sm">
                {currentPlayer?.currentMarketValue && (
                  <span className="font-black text-xl" style={{ color: '#00d68f' }}>&euro;{currentPlayer?.currentMarketValue}M</span>
                )}
              </div>
              <div className="flex gap-3">
                {onRequestConsultation && (
                  <button
                    onClick={() => onRequestConsultation(currentPlayer)}
                    className="font-bold py-2.5 px-6 rounded-xl transition-all hover:opacity-90 flex items-center gap-2 text-sm text-white"
                    style={{ background: 'linear-gradient(135deg, #FF6B00, #FF9500)', boxShadow: '0 4px 15px rgba(255,107,0,0.35)' }}
                  >
                    <Icon name="MessageSquare" size={16} />
                    Request Consultation
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="font-semibold py-2.5 px-6 rounded-xl transition-all hover:opacity-80 text-sm"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.65)' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default PlayerDetailModal;